import React, { useState } from "react";

import type { IVerifyEnrollmentAndAuthorizeCriteria } from "@mne-authentication/api";
import { useSearchParams } from "react-router";

import { FormattedMessage } from "@mnemonic/intl";
import { getTargetMeta } from "@mnemonic/pure-utils";

import { commonMessages } from "../../commonMessages";
import type { TAuthorization } from "../../utils/authStateUtils/authStateTypes";

type TEnrollFlowVerifyUserState = {
  kind: "verifyUser";
};

type TEnrollFlowVerifySmsCodeState = {
  kind: "verifySmsCode";
};

type TEnrollFlowDefinePasswordState = {
  kind: "definePassword";
};

type TEnrollFlowCompleteState = {
  kind: "complete";
};

type TEnrollFlowState = {
  step:
    | TEnrollFlowVerifyUserState
    | TEnrollFlowVerifySmsCodeState
    | TEnrollFlowDefinePasswordState
    | TEnrollFlowCompleteState;

  enrollmentCode: string; //The enrollment code sent to user's email
  userName: string;

  activationCookie?: IVerifyEnrollmentAndAuthorizeCriteria["activationCookie"];
  userId?: number;
  setPasswordAuthorization?: {
    authorization: string;
    expires: number;
    sessionKey: string;
  };
  authorizations?: TAuthorization[];
};

export interface IEnrollUsernamePassword {
  activeStep: number;
  autoSubmit: boolean;
  enrollFlowState: TEnrollFlowState;
  domain: string;
  setEnrollFlowState: React.Dispatch<React.SetStateAction<TEnrollFlowState>>;
  steps: {
    key: string;
    label: React.JSX.Element;
  }[];
}

const initialFlowState: TEnrollFlowVerifyUserState = { kind: "verifyUser" };

export const useVerifyUsernamePassword = (): IEnrollUsernamePassword => {
  const [searchParams] = useSearchParams();
  const userName = searchParams.get("userName") || null;
  const enrollmentCode = searchParams.get("enrollmentCode") || null;
  const [enrollFlowState, setEnrollFlowState] = useState<TEnrollFlowState>({
    step: initialFlowState,
    userName: userName,
    enrollmentCode: enrollmentCode,
  });
  const { metaInfo } = getTargetMeta();
  const domain = metaInfo.theme === "mnemonic" ? "MNEMONIC" : "NFCERT";
  const autoSubmit = Boolean(userName) && Boolean(enrollmentCode);

  const steps = [
    { key: "verifyUser", label: <FormattedMessage {...commonMessages.EnrollmentCode} /> },
    { key: "verifySmsCode", label: <FormattedMessage {...commonMessages.VerificationCode} /> },
    { key: "definePassword", label: <FormattedMessage {...commonMessages.SetPassword} /> },
  ];

  const activeStep = steps.findIndex((step) => step.key === enrollFlowState.step.kind);

  return {
    activeStep,
    autoSubmit,
    domain,
    enrollFlowState,
    setEnrollFlowState,
    steps,
  };
};
